import React, { useEffect, useState } from "react";
import "./MyBallots.css";
import { getMyVotes, getSubBallotsByVote } from "../../api/Vote";
import { getUserId, getToken } from "../../api/index";
import Loader from "../Loader";
import { getHumanizedTime } from "../getHumanizedTime";
import MyBallotsModal from "./MyBallotsModal";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";

function MyBallots() {
    const [allVotes, setAllVotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [subBallots, setSubBallots] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [unmutedVideoId, setUnmutedVideoId] = useState(null);
    const [playingVideos, setPlayingVideos] = useState({});
    const [selectedParentBallot, setSelectedParentBallot] = useState(null);

    useEffect(() => {
        fetchMyVotes();
    }, []);

    const fetchMyVotes = async () => {
        setIsLoading(true);
        const userId = getUserId();
        const token = getToken();
        try {
            const response = await getMyVotes({
                userId: userId,
                skip: 0,
                take: 50, // Taking more for "My Ballots" view
            });
            console.log("My votes response:", response);
            if (response && response.success) {
                setAllVotes(response.data);
            }
        } catch (error) {
            console.error("Error fetching my votes:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewFollowup = async (ballot) => {
        setSelectedParentBallot(ballot);
        setIsModalOpen(true);
        setModalLoading(true);
        const userId = getUserId();
        try {
            const response = await getSubBallotsByVote(ballot.voteId, userId);
            console.log("Sub-ballots response:", response);
            if (response && response.success) {
                setSubBallots(response.data);
            } else {
                setSubBallots([]);
            }
        } catch (error) {
            console.error("Error fetching sub-ballots:", error);
            setSubBallots([]);
        } finally {
            setModalLoading(false);
        }
    };

    const handleVolumeToggle = (voteId, event) => {
        event.stopPropagation();
        setUnmutedVideoId(unmutedVideoId === voteId ? null : voteId);
    };

    const handleVideoPlayPause = (voteId, event) => {
        event.stopPropagation();
        const videoElement = event.target
            .closest(".voteMediaContainer")
            .querySelector("video");

        if (videoElement) {
            if (playingVideos[voteId]) {
                videoElement.pause();
                setPlayingVideos((prev) => ({ ...prev, [voteId]: false }));
            } else {
                videoElement.play();
                setPlayingVideos((prev) => ({ ...prev, [voteId]: true }));
            }
        }
    };

    // Helper function to get user initials
    const getUserInitials = (userName) => {
        if (!userName) return "";
        const words = userName.trim().split(" ");
        if (words.length === 1) {
            return words[0].charAt(0).toUpperCase();
        }
        const firstInitial = words[0] ? words[0].charAt(0).toUpperCase() : "";
        const lastInitial = words[words.length - 1]
            ? words[words.length - 1].charAt(0).toUpperCase()
            : "";
        return firstInitial + lastInitial;
    };

    // Helper function to generate background color based on initials
    const getBackgroundColor = (initials) => {
        const colors = [
            "#FF6B6B",
            "#4ECDC4",
            "#45B7D1",
            "#96CEB4",
            "#FFEAA7",
            "#DDA0DD",
            "#98D8C8",
            "#F7DC6F",
            "#BB8FCE",
            "#85C1E9",
            "#F8C471",
            "#82E0AA",
        ];

        let hash = 0;
        for (let i = 0; i < initials.length; i++) {
            hash = initials.charCodeAt(i) + ((hash << 5) - hash);
        }
        hash = Math.abs(hash);
        return colors[hash % colors.length];
    };

    return (
        <div className="MyBallots Feeds">
            <div className="feedsContainer">
                {isLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '100%', gap: '20px' }}>
                        <Loader containerSize="3rem" />
                        <p style={{
                            color: '#ffffff',
                            fontWeight: '600',
                            fontSize: '1rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1.5px',
                            textAlign: 'center',
                            maxWidth: '90%',
                            lineHeight: '1.4',
                            fontFamily: '"Inter", sans-serif',
                            opacity: 0.8
                        }}>
                            Hang tight, we’re getting things ready for you...
                        </p>
                    </div>
                ) : allVotes.length === 0 ? (
                    <div
                        className="feedCardContainer"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "600px",
                            textAlign: "center",
                            padding: "40px 20px",
                        }}
                    >
                        <div className="noVotesMessage">
                            <h3 className="noVotesMessageText">No Ballots Found</h3>
                            <p className="noVotesMessageText2">
                                You haven't created any ballots yet.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="feedCardContainer">
                        {allVotes.map((item, index) => (
                            <div key={item.voteId} className="feedCard">
                                <div className="feedBody">
                                    <div className="feedCardHeader">
                                        <div className="profileDetailsContainer">
                                            {item.createdByUserPhoto ? (
                                                <img
                                                    src={item.createdByUserPhoto}
                                                    alt="Profile"
                                                    className="profilePic"
                                                />
                                            ) : (
                                                <div
                                                    className="profilePic"
                                                    style={{
                                                        backgroundColor: getBackgroundColor(
                                                            getUserInitials(item.createdByUserName)
                                                        ),
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "16px",
                                                    }}
                                                >
                                                    {getUserInitials(item.createdByUserName)}
                                                </div>
                                            )}
                                            <div className="profileDetails">
                                                <span className="profileNameText">
                                                    {item.createdByUserName}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="timeContainer">
                                            <span className="timeText">
                                                {getHumanizedTime(item.createdOn)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="voteTitleContainer">
                                        <div className="voteTitleTextContainer">
                                            <span className="voteTitleText">{item.title}</span>
                                        </div>
                                    </div>

                                    {item.imageUrls?.length > 0 && (
                                        <div className="voteMediaContainer">
                                            <img
                                                src={item.imageUrls[0]}
                                                alt="Ballot"
                                                className="voteMedia"
                                            />
                                        </div>
                                    )}

                                    {item.videoUrl && (
                                        <div
                                            className="voteMediaContainer"
                                            style={{ position: "relative" }}
                                        >
                                            <video
                                                src={item.videoUrl}
                                                className="voteMedia"
                                                muted={unmutedVideoId !== item.voteId}
                                                loop
                                                onClick={(e) => handleVideoPlayPause(item.voteId, e)}
                                                style={{ cursor: "pointer" }}
                                            />
                                            {!playingVideos[item.voteId] && (
                                                <div
                                                    className="playPauseOverlay"
                                                    style={{
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        zIndex: 10,
                                                    }}
                                                    onClick={(e) => handleVideoPlayPause(item.voteId, e)}
                                                >
                                                    <PlayArrowRoundedIcon
                                                        sx={{ color: "#5AAF9A", fontSize: "70px" }}
                                                    />
                                                </div>
                                            )}
                                            <div
                                                className="volumeControlContainer"
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    background: "rgba(0, 0, 0, 0.5)",
                                                    borderRadius: "50%",
                                                    padding: "8px",
                                                    display: "flex",
                                                }}
                                                onClick={(e) => handleVolumeToggle(item.voteId, e)}
                                            >
                                                {unmutedVideoId === item.voteId ? (
                                                    <VolumeUpIcon
                                                        sx={{ color: "#fff", fontSize: "20px" }}
                                                    />
                                                ) : (
                                                    <VolumeOffIcon
                                                        sx={{ color: "#fff", fontSize: "20px" }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {item.description && (
                                        <div className="voteDescriptionContainer">
                                            <span className="voteDescriptionText">
                                                {item.description}
                                            </span>
                                        </div>
                                    )}

                                    <div className="voteBtnsContainer">
                                        {item.options.map((option) => (
                                            <div key={option.id} className="vote-button-wrapper voted">
                                                <div
                                                    className="vote-progress-bar"
                                                    style={{ width: `${option.percentage}%` }}
                                                />
                                                <span className="vote-text">{option.value}</span>
                                                <span className="vote-percentage">
                                                    {Math.round(option.percentage)}%
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {item.haveSubBallot && (
                                        <div
                                            className="view-followup-container"
                                            style={{ marginTop: "15px" }}
                                        >
                                            <button
                                                className="view-followup-btn"
                                                onClick={() => handleViewFollowup(item)}
                                            >
                                                View followup question
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <MyBallotsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                subBallots={subBallots}
                isLoading={modalLoading}
                parentTitle={selectedParentBallot?.title}
            />
        </div>
    );
}

export default MyBallots;
